import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Tooltip, Popover } from 'antd';
import { tokenColors } from '../../../utils/tokenIcons';
import TokenIcon from '../../TokenIcon';
import formatCurrency from '../../../utils/currencyFormatter';
import useWindowSize from '../../../utils/resizeHook';
import { walletTruncate } from '../../../utils/truncateFunction';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Copy } from '../../../img/default-svg/copy.svg';
import { ReactComponent as CopyInex } from '../../../img/inex/copy.svg';
import { ReactComponent as CopyInsta } from '../../../img/instaXchange/copy.svg';
import { ReactComponent as CopyCoinera } from '../../../img/coinera/copy.svg';
import { getAssetDisplaySuffix } from '../../../utils/tokenNodeTypes';
import { identity } from "../../../utils/getIdentity";

const copyIcon = {
  cpay: <Copy />,
  nfg: <Copy />,
  finvaro: <Copy />,
  clarnium: <Copy />,
  inex: <CopyInex />,
  insta: <CopyInsta />,
  coinera: <CopyCoinera />
}

const Wallet = ({ wallet, selectedWallet, setSelectedWallet, getWalletInfo }) => {
  const [isTokensOpened, setIsTokensOpened] = useState(true);
  const { currentWidth } = useWindowSize();
  const { t } = useTranslation();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const walletAddressRef = useRef(null);
  const [tooltipVisibility, setTooltipVisibility] = useState({});
  const [showAddressPopover, setShowAddressPopover] = useState(false);
  const addressPopoverTimer = useRef(null);
  const [popoverVisibility, setPopoverVisibility] = useState({});
  const [showTooltip, setShowTooltip] = useState({});
  const walletAssets = (() => {
    if (wallet?.balances?.length > 0) {
      return wallet.balances.map(asset => ({
        currency: asset?.currency?.name,
        currencyId: asset?.currency?.id,
        currencyType: asset?.currency?.currencyType,
        nodeType: asset?.currency?.nodeType,
        holdBalance: asset?.holdBalance,
        balance: asset?.balance,
      }));
    }
    const assets = [];
    if (wallet?.currency) {
      assets.push({
        currency: wallet.currency,
        currencyId: wallet.currencyId,
        currencyType: wallet.currencyType || 'currency',
        nodeType: wallet.nodeType,
        balance: wallet.balance,
      });
    }
    if (wallet?.tokens?.length > 0) {
      wallet.tokens.forEach(token => {
        assets.push({
          currency: token.currency,
          currencyId: token.currencyId,
          currencyType: 'token',
          nodeType: wallet.nodeType,
          holdBalance: token.holdBalance,
          balance: token.balance,
        });
      });
    }
    return assets;
  })();
  const nativeAsset = walletAssets?.find(asset => asset?.currencyType === 'currency');
  const walletCurrency =
    nativeAsset?.currency ||
    wallet?.currency ||
    walletAssets?.[0]?.currency ||
    (wallet?.chainSymbol && wallet.chainSymbol.toLowerCase() !== 'evm' ? wallet.chainSymbol : '');
  const nativeAssetIndex = nativeAsset ? walletAssets.indexOf(nativeAsset) : -1;
  const dropdownAssets = walletAssets.filter((_, i) => i !== nativeAssetIndex);
  const hasMultipleDropdownItems = dropdownAssets.length > 1;
  const hasDropdownItems = dropdownAssets.length > 0;

  const walletBaseBalance =
    nativeAsset?.balance ||
    wallet?.balance ||
    (walletAssets?.length
      ? {
          value: walletAssets.reduce((sum, a) => sum + Number(a?.balance?.value || 0), 0),
          usd: walletAssets.reduce((sum, a) => sum + Number(a?.balance?.usd || 0), 0),
        }
      : { value: 0, usd: 0 });

  const addressCopyHandler = value => {
    navigator.clipboard.writeText(value);
    setShowAddressPopover(true);
    setShowTooltip({});

    addressPopoverTimer.current = setTimeout(() => {
      setShowAddressPopover(false);
      setPopoverVisibility({ ...popoverVisibility, [wallet._id]: false }); // Hide the Popover after 2 seconds
    }, 2000);
    addressPopoverTimer.current = setTimeout(() => setShowAddressPopover(false), 1000);
  };

  const isSelected =
    selectedWallet && !selectedWallet.hasOwnProperty('holdBalance') && wallet._id === selectedWallet._id;

  const isExpanded = hasMultipleDropdownItems ? isTokensOpened : hasDropdownItems;

  useEffect(() => {
    if (
      !isExpanded &&
      selectedWallet &&
      selectedWallet.hasOwnProperty('holdBalance') &&
      selectedWallet.address === wallet.address
    ) {
      setSelectedWallet(null);
    }
  }, [isExpanded]);

  useEffect(() => {
    const element = walletAddressRef.current;

    const checkOverflow = () => {
      if (element) {
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [wallet.address, selectedWallet]);

  useEffect(() => {
    return () => {
      clearTimeout(addressPopoverTimer);
      addressPopoverTimer.current = null;
    };
  });

  return (
    <div
      className={`withdraw-wallets__wallet withdraw-wallets__wallet-${identity} ${hasDropdownItems ? 'withdraw-wallets__wallet_hasTokens' : ''}`}
      onMouseEnter={e => (e.currentTarget.style.borderColor = tokenColors(walletCurrency).color)}
      onMouseLeave={e =>
        (e.currentTarget.style.borderColor = (!hasDropdownItems || !isExpanded) && isSelected ? 'transparent' : '#E0E6ED')
      }
      style={
        (!hasDropdownItems || !isExpanded) && isSelected
          ? {
              background: tokenColors(walletCurrency).bgColor,
              borderColor: 'transparent',
            }
          : {
              background: 'transparent',
              borderColor: '#E0E6ED',
            }
      }
    >
      {hasMultipleDropdownItems ? (
        <div
          className={`withdraw-wallets__dropdown-icon ${
            isTokensOpened ? 'withdraw-wallets__dropdown-icon_opened' : ''
          }`}
          onClick={() => setIsTokensOpened(!isTokensOpened)}
        >
          <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 6.12598L8 10.126L12 6.12598"
              stroke={tokenColors(walletCurrency).color}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ) : (
        ''
      )}
      <Row
        align="middle"
        onClick={() => {
          setSelectedWallet(wallet);
        }}
        style={
          hasDropdownItems && isExpanded && isSelected
            ? {
                background: tokenColors(wallet.currency).bgColor,
                borderColor: 'transparent',
              }
            : {
                background: 'transparent',
                borderColor: '#E0E6ED',
              }
        }
      >
        <Col span={currentWidth >= 1024 ? 1 : 3}>
          <TokenIcon tokenName={walletCurrency} className="withdraw-wallets__logo" />
        </Col>
        <Col span={currentWidth >= 1024 ? 14 : 21}>
          <div className="withdraw-wallets__address" style={{ color: tokenColors(walletCurrency).color }}>
            <span className="withdraw-wallets__address-text" ref={walletAddressRef}>
              {' '}
              {wallet?.address}
            </span>

            {isOverflowing && (
              <Tooltip
                title={wallet.address}
                visible={tooltipVisibility[wallet._id]}
                onVisibleChange={isVisible => setTooltipVisibility({ ...tooltipVisibility, [wallet._id]: isVisible })}
              >
                <span
                  className="withdraw-wallets__address-mark"
                  onMouseEnter={() => setTooltipVisibility({ ...tooltipVisibility, [wallet._id]: true })}
                  onMouseLeave={() => setTooltipVisibility({ ...tooltipVisibility, [wallet._id]: false })}
                >
                  *
                </span>
              </Tooltip>
            )}
            {selectedWallet &&
            selectedWallet._id === wallet._id &&
            !selectedWallet.hasOwnProperty('holdBalance') ? null : (
              <Tooltip
                title={t('clickToCopy')}
                placement="top"
                visible={showTooltip[wallet._id] || false}
                onVisibleChange={visible => setShowTooltip({ ...showTooltip, [wallet._id]: visible })}
              >
                <Popover
                  title=""
                  content={t('copied')}
                  trigger="click"
                  visible={popoverVisibility[wallet._id] || false}
                  onVisibleChange={visible => setPopoverVisibility({ ...popoverVisibility, [wallet._id]: visible })}
                >
                  <div
                    className="withdraw-wallets__address-copy"
                    onClick={e => {
                      e.stopPropagation();
                      addressCopyHandler(wallet.address);
                    }}
                  >
                    {copyIcon[identity]}
                  </div>
                </Popover>
              </Tooltip>
            )}

            {selectedWallet && selectedWallet._id === wallet._id && !selectedWallet.hasOwnProperty('holdBalance') && (
              <button
                className={`withdraw-wallets__address-button withdraw-wallets__address-button-${identity}`}
                type="button"
                onClick={() => getWalletInfo(wallet._id, wallet.address)}
                disabled={walletBaseBalance.usd === 0}
              >
                {t('send')}
              </button>
            )}
          </div>
        </Col>
        <Col span={currentWidth >= 1024 ? 9 : 21} offset={currentWidth >= 1024 ? 0 : 3}>
          <div className="withdraw-wallets__balance">
            <div className={`withdraw-wallets__balance-token withdraw-wallets__balance-token-${identity}`}>
              {walletBaseBalance.value} {walletCurrency}{' '}
              {(() => { const s = nativeAsset ? getAssetDisplaySuffix(nativeAsset.currencyType, nativeAsset.nodeType, nativeAsset.currency) : ''; return s ? `(${s})` : ''; })()}
            </div>
            <div className="withdraw-wallets__balance-usd">{formatCurrency(walletBaseBalance.usd)}</div>
          </div>
        </Col>
      </Row>
      {isExpanded && hasDropdownItems && (
        <div className="withdraw-wallets__tokens-dropdown">
          {dropdownAssets.map((asset, index) => {
            const isItemSelected =
              selectedWallet && selectedWallet.currencyId === asset.currencyId && wallet._id === selectedWallet._id;

            return (
              <Row
                key={wallet._id + index}
                align="middle"
                className="withdraw-wallets__token"
                onClick={() => {
                  setSelectedWallet({ ...asset, _id: wallet._id, address: wallet.address, nodeType: wallet.nodeType });
                }}
                style={{ background: isItemSelected ? tokenColors(asset.currency).bgColor : 'transparent' }}
              >
                <Col span={currentWidth >= 1024 ? 1 : 3}>
                  <TokenIcon tokenName={asset.currency} className="withdraw-wallets__logo" />
                </Col>
                <Col span={currentWidth >= 1024 ? 13 : 21}>
                  <div className="withdraw-wallets__address" style={{ color: tokenColors(asset.currency).color }}>
                    <span className="withdraw-wallets__address-text">{wallet.address}</span>
                    {selectedWallet &&
                    selectedWallet._id === wallet._id &&
                    selectedWallet.currencyId === asset.currencyId ? null : (
                      <Tooltip
                        title={t('clickToCopy')}
                        visible={showTooltip[asset.currency] || false}
                        onVisibleChange={visible => setShowTooltip({ ...showTooltip, [asset.currency]: visible })}
                        placement="top"
                      >
                        <Popover
                          title=""
                          content={t('copied')}
                          trigger="click"
                          visible={popoverVisibility[asset.currency] || false}
                          onVisibleChange={visible =>
                            setPopoverVisibility({ ...popoverVisibility, [asset.currency]: visible })
                          }
                        >
                          <div
                            className="withdraw-wallets__address-copy"
                            onClick={e => {
                              e.stopPropagation();
                              addressCopyHandler(wallet.address);
                            }}
                          >
                            {copyIcon[identity]}
                          </div>
                        </Popover>
                      </Tooltip>
                    )}

                    {selectedWallet &&
                      selectedWallet._id === wallet._id &&
                      selectedWallet.currencyId === asset.currencyId && (
                        <button
                          className={`withdraw-wallets__address-button withdraw-wallets__address-button-${identity}`}
                          type="button"
                          onClick={() => getWalletInfo(wallet._id, wallet.address)}
                        >
                          {t('send')}
                        </button>
                      )}
                  </div>
                </Col>
                <Col span={currentWidth >= 1024 ? 10 : 21} offset={currentWidth >= 1024 ? 0 : 3}>
                  <div className="withdraw-wallets__balance">
                    <div className={`withdraw-wallets__balance-token withdraw-wallets__balance-token-${identity}`}>
                      {asset.balance.value} {asset.currency}{(() => { const s = getAssetDisplaySuffix(asset.currencyType, asset.nodeType, asset.currency); return s ? ` (${s})` : ''; })()}
                    </div>
                    <div className="withdraw-wallets__balance-usd">{formatCurrency(asset.balance.usd)}</div>
                  </div>
                </Col>
              </Row>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Wallet;
